<template>
  <div class="card">
    <div class="card__image" :class="{ noContent: !postImages }">
      <img
        v-if="postImages"
        :src="postImages[0].url"
        alt=""
      >
      <p v-else class="noimage">NO IMAGE</p>
    </div>

    <div class="card__content">
      <div class="upperRow">
        <span v-if="topicType && topicTypes[topicType]" class="topic-type">
          {{ topicTypes[topicType] }}
        </span>
        <KkCategoryTip
          :name="category.name"
          :is-editable="selectedPostStatusType === 'POST'"
          @click="showCategoryEditModal = !showCategoryEditModal"
        />
      </div>

      <KkPostTitleText :topic-type="topicType" :post-title="postTitle" :post-event-title="event?.title" />

      <p v-if="summary" class="summary"><KkMore :text="summary" :length="moreDisplayLength" :is-expand-html="false" /></p>

      <p v-if="couponCode" class="coupon-code">
        クーポンコード：<span>{{ couponCode }}</span>
      </p>

      <p v-if="event" class="event-period">
        {{ topicType === 'EVENT' ? '期間' : '有効期限' }}：{{ eventPeriod(event.startedAt, event.endedAt) }}
      </p>

      <span
        v-if="callToAction"
        class="link"
        @click="openPreviewModal"
      >
        {{ selectActionType(callToAction.actionType) }}
      </span>
    </div>

    <div class="card__detail" :class="{ 'card__detail--slim': selectedPostStatusType === 'POST' }">
      <div class="published">
        {{ $t('pages.CompaniesStorePost.scheduledAt') }}：<time class="published__at">{{ publishedDateTime }}</time>
        <span class="published__status" :class="{ future: publishedStatus() === '投稿予約' }">
          {{ publishedStatus() }}
        </span>
      </div>

      <div class="store">
        <button @click="openPostStoresModal">投稿店舗：<span>{{ storeCount.all }}店舗</span></button>
      </div>

      <template v-if="publishedStatus() === '投稿予約'">
        <div class="c-info">
          <Icon name="iconCheck" class="c-button__icon" />
          <p class="c-info__content">予約時間に投稿されます</p>
        </div>
        <div v-if="repeatConfiguration" class="c-info repeatingPost">
          <p class="c-info__content">{{ $t('pages.CompaniesStorePost.repeatingPost') }}：<span>{{ repeatingPostName }}</span></p>
        </div>
      </template>
      <template v-else>
        <p class="statusMsg" :class="{ 'statusMsg__error': status === 'error' }">
          <span :class="`statusMark__${status}`" class="statusMark" />
          {{ statusMsg }}
        </p>
      </template>

      <div v-if="postApprovalFlow && approverName" class="stakeholder">
        {{ approver }}
      </div>
      <div v-if="postApprovalFlow && applicantName" class="stakeholder">
        {{ applicant }}
      </div>
      <div class="stakeholder">
        {{ author }}
      </div>

      <!-- サービス一覧 -->
      <div v-if="services.length" class="services">
        <ul class="services__group">
          <li
            v-for="service in services"
            :key="service"
            class="services__item"
          >
            <KkSiteIcon :name="service" />
          </li>
        </ul>
      </div>
    </div>

    <div class="card__action c-button c-button--icon">
      <router-link
        v-if="canEdit"
        :to="{ name: 'CompaniesStorePostsEdit', params: { company_uuid: currentCompanyUuid, post_id: id } }"
        class="c-button__item"
        @click="handleEdit"
      >
        <KkIcon name="edit" color="rgba(58, 59, 63, 0.7)" class="c-button__icon" />
        編集する
      </router-link>

      <button class="c-button__item" @click="openPreviewModal">
        <KkIcon name="detail" color="rgba(58, 59, 63, 0.7)" class="c-button__icon" />
        プレビュー
      </button>

      <router-link
        v-if="showMoreThanOperatorAuthority"
        :to="{ name: 'CompaniesStorePostsNew', params: { company_uuid: currentCompanyUuid}, query: { copy: id } }"
        class="c-button__item"
        @click="handleCopy"
      >
        <KkIcon name="copy" color="rgba(58, 59, 63, 0.7)" class="c-button__icon" />
        複製する
      </router-link>

      <button v-if="showCloseButton" class="c-button__item" @click="handleFinish(id)">
        <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" class="c-button__icon" />
        公開終了する
      </button>
      <button v-if="showDeleteButton" class="c-button__item" @click="deletePost(id)">
        <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" class="c-button__icon" />
        削除する
      </button>
    </div>
    <KkCategoryEditModal
      :show-modal="showCategoryEditModal"
      :category-id="category.id"
      :post-id="id"
      @submit="handleUpdateCategory"
      @close="showCategoryEditModal = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import Icon from '../Icon'
import { KkCategoryEditModal } from '../domains/promotion/CompaniesStorePosts'
import { KkIcon, KkSiteIcon } from '../../components/base'
import { dayOfWeek } from '../../modules/dayOfWeekConst'
import { i18n } from '../../plugins/i18n'
import { KkCategoryTip } from '../domains/sns'
import KkMore from '../base/KkMore/index.vue'
import { actionEvent } from '../../modules/googleAnalytics.ts'
import { KkPostTitleText } from '../../domains/post'

export default {
  name: 'Card',
  components: {
    Icon,
    KkIcon,
    KkSiteIcon,
    KkCategoryTip,
    KkCategoryEditModal,
    KkMore,
    KkPostTitleText
  },
  props: {
    id: Number,
    storeErrorCount: Number,
    status: String,
    topicType: String,
    summary: String,
    couponCode: String,
    postTitle: String,
    event: Object,
    callToAction: Object,
    postImages: Array,
    scheduledAt: Date,
    localPostViewsSearch: Number,
    localPostActionsCallToAction: Number,
    canDeletePostAuthority: Boolean,
    postApprovalFlow: Boolean,
    storeCount: {
      type: Object,
      required: true
    },
    authorName: {
      type: String,
      required: true
    },
    approverName: {
      type: String,
      default: ''
    },
    applicantName: {
      type: String,
      default: ''
    },
    showOnlyOwnerAuthority:Boolean,
    repeatConfiguration: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: false
    },
    category: {
      type: Object,
      default: () => {}
    },
    selectedPostStatusType: {
      type: String,
      default: ''
    },
  },
  emits: ['submit-category', 'delete-post', 'open-post-stores-modal', 'open-preview-action-modal'],
  data() {
    return {
      showCategoryEditModal: false,
      moreDisplayLength: 40,
    }
  },
  computed: {
    ...mapGetters('companiesStorePosts',['selectActionType', 'topicTypes']),
    ...mapGetters({
      showMoreThanOperatorAuthority: 'companiesAccessibleApi/showMoreThanOperatorAuthority'
    }),
    publishedDateTime() {
      if (!this.scheduledAt) return ''

      const dayOfWeek = this.dayOfWeekOfReservationDate.charAt(0)
      return moment(this.scheduledAt).format(`${i18n.t('format.date.default')}(${dayOfWeek}) ${i18n.t('format.time.default')}`)
    },
    dayOfWeekOfReservationDate() {
      const dowNumber = moment(this.scheduledAt).day()
      return Object.values(dayOfWeek)[dowNumber]
    },
    dayOfWeekCountOfReservationDate() {
      return Math.floor((moment(this.scheduledAt).toDate().getDate() - 1) / 7) + 1
    },
    repeatingPostName() {
      switch (this.repeatConfiguration) {
      case 'daily':
        return '毎日'
      case 'weekly':
        return `毎週 ${this.dayOfWeekOfReservationDate}`
      case 'wdayIndex':
        return `毎月 第${this.dayOfWeekCountOfReservationDate}${this.dayOfWeekOfReservationDate}`
      case 'yearly':
        return `毎年 ${moment(this.scheduledAt).format('M月D日')}`
      case 'weekday':
        return '毎週 (月〜金)'
      case 'monthly':
        return `毎月 ${moment(this.scheduledAt).format('D日')}`
      }
      return null
    },
    statusMsg() {
      if (this.status === 'error') {
        return this.$t('pages.CompaniesStorePost.index.status.' + this.status, { count: this.storeErrorCount })
      }
      return this.$t('pages.CompaniesStorePost.index.status.' + this.status)
    },
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    author() {
      return this.$t('pages.CompaniesStorePost.index.authorName', { author: this.authorName })
    },
    approver() {
      if(this.postApprovalFlow && this.approverName) {
        return this.$t('pages.CompaniesStorePost.index.approverName', { approver: this.approverName })
      }
      return null
    },
    applicant() {
      if(this.postApprovalFlow && this.applicantName) {
        return this.$t('pages.CompaniesStorePost.index.applicantName', { applicant: this.applicantName })
      }
      return null
    },
    canDelete() {
      return this.showMoreThanOperatorAuthority && this.editable
    },
    canEdit() {
      if(!this.showMoreThanOperatorAuthority) {
        return false
      }

      // そもそも投稿予約時間が入っていないケースは編集不可(即時投稿のケース)
      if(!this.scheduledAt){
        return false
      }

      // 投稿予約時間を過ぎるまでは編集可能
      if(this.scheduledAt.getTime() - new Date().getTime() > 0) {
        if(this.postApprovalFlow){
          // 承認機能が有効な場合は、「編集」と「削除」はオーナーのみ
          return this.showOnlyOwnerAuthority
        }

        return this.editable
      }

      return false
    },
    // 公開待ちの投稿は削除ボタンを表示
    showDeleteButton() {
      return this.canDeletePostAuthority && this.canDelete && this.status === 'future'
    },
    // 同期中と公開終了した投稿（+公開待ちの投稿）には公開終了ボタンは表示しない
    // エラー投稿、投稿完了しているものには公開終了ボタン表示
    showCloseButton() {
      if (this.status === 'progress' || this.status === 'closed' || this.status === 'future') return false
      return this.canDeletePostAuthority && this.canDelete
    },
    services() {
      const services = []
      if (this.storeCount.googleMap > 0) services.push('google')
      if (this.storeCount.docomo > 0) services.push('docomo')
      if (this.storeCount.yahooMap > 0) services.push('yahoo_map')

      return services
    }
  },
  methods: {
    ...mapActions({
      updateSubmitActionModalType: 'companiesStorePosts/updateSubmitActionModalType',
    }),
    publishedStatus() {
      return this.status === 'future' ? '投稿予約' : ''
    },
    deletePost(id) {
      this.$emit('delete-post', id)
    },
    openPreviewModal() {
      this.updateSubmitActionModalType('')
      this.$emit('open-preview-action-modal', { postId: this.id })
    },
    async openPostStoresModal() {
      this.$emit('open-post-stores-modal', this.id)
    },
    eventPeriod(eventStartedAt, eventEndedAt) {
      const start = moment(new Date(eventStartedAt))
      const end = moment(new Date(eventEndedAt))
      let startedAt
      let endedAt

      if (start.format('HH:mm:ss') === '00:00:00' && end.format('HH:mm:ss') === '23:59:59')  {
        startedAt = start.format(i18n.t('format.date.default'))
        endedAt = end.format(i18n.t('format.date.default'))
      } else {
        startedAt = start.format(i18n.t('format.datetime.default'))
        endedAt = end.format(i18n.t('format.datetime.default'))
      }

      return startedAt + ' - ' + endedAt
    },
    handleUpdateCategory({ postId, payload }) {
      this.$emit('submit-category', { postId, payload })
    },
    handleFinish(id) {
      this.deletePost(id)
      if (this.selectedPostStatusType === 'POST') {
        actionEvent('FinishPost')
      }
    },
    handleCopy() {
      if (this.selectedPostStatusType === 'POST') {
        actionEvent('CopyPost')
      }
    },
    handleEdit() {
      if (this.selectedPostStatusType === 'RESERVE') {
        actionEvent('EditPost')
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 8px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
  }

  &__image {
    flex: none;
    margin-right: 24px;
    width: 20%;
    height: 120px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;

    @media screen and (max-width: 768px) {
      width: 100%;
      margin-right: 0;
    }

    &.noContent {
      display: flex;
      justify-content: center;
      align-items: center;

      @media screen and (max-width: 768px) {
        display: none;
      }
    }

    img {
      display: block;
      object-fit: contain;
      width: 100%;
      height: 100%;
    }

    .noimage {
      color: #979797;
      letter-spacing: 3px;
    }
  }

  &__content {
    width: 40%;
    margin-right: 40px;

    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }

    .topic-type {
      color: white;
      font-weight: bold;
      font-size: 12px;

      border-radius: 12px;
      background-color: #3A3B3F;
      padding: 4px 6px;
      margin-right: 5px;
    }
    .upperRow {
      display: flex;
      align-items: center;
    }

    .summary {
      white-space: pre-wrap;
      word-break: break-all;
    }

    .coupon-code,
    .event-period {
      font-size: 12px;
      margin-top: 8px;
    }

    .coupon-code {
      span {
        font-weight: bold;
        word-break: break-all;
      }
    }

    .link {
      display: inline-block;
      margin-top: 10px;
      padding: 8px 16px;
      color: #3A3B3F;
      font-size: 12px;
      line-height: 1.17;
      background-color: #EEE;
      border-radius: 4px;
      text-decoration: none;
      cursor: pointer;
      transition: background-color 0.2s;
      &:hover {
        background-color: #E4E7EB;
      }

      @media screen and (max-width: 768px) {
        margin-top: 8px;
      }
    }
  }

  &__detail {
    flex: none;
    width: 260px;
    margin-right: 40px;

    &--slim {
      width: 220px;
      margin-right: 20px;
    }

    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;

      .c-info {
        margin: 0;
      }
    }

    .published {
      display: flex;
      align-items: center;
      font-size: 12px;
      line-height: 1.5;

      &__status {
        margin-left: 16px;
        font-weight: bold;

        &.future {
          display: inline-flex;
          align-items: center;
          height: 20px;
          margin-left: 8px;
          padding: 0 6px;
          border-radius: 10px;
          background-color: #E9446A;
          color: #FFF;
          font-size: 10px;
          line-height: 1;
        }
      }
    }

    .store {
      margin: 4px 0 10px;
      font-size: 12px;
      line-height: 1.17;

      span {
        color: #E9446A;
        font-weight: bold;
        text-decoration: underline;
      }
    }

    .stakeholder {
      font-size: 12px;
    }

    .services {
      margin-top: 8px;

      &__group {
        display: flex;
        flex-wrap: wrap;
      }

      &__item {
        width: 38px;
        height: 38px;
        padding: 4px;
        margin: 0 4px 4px 0;
        border: 1px solid rgba(228,231,235,.5);
        border-radius: 8px;
      }
    }
  }

  &__action {
    display: flex;
    flex-direction: column;
    flex: none;
    align-items: flex-start;

    @media screen and (max-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      margin-top: 16px !important;
    }

    &.c-button {
      margin-top: 0;
    }
  }

  .p-modal__content__body {
    ul {
      padding-left: 24px;

      li {
        list-style: disc;
      }
    }
  }
}
.repeatingPost {
  font-weight: bold;
  color: #FFFFFF;
  background-color: #3A3B3F;
}
.statusMsg__error {
  color: $color-accent-primary;
}
.statusMark {
  border-radius: 50%;
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 2px;
  &__error {
    background: $color-accent-primary;
  }
  &__complete {
    background: $complete-color;
  }
  &__progress {
    background: #D2D5DB;
  }
  &__closed {
    background: #D2D5DB;
  }
}
:deep(.category) {
  font-size: 12px;
  margin-bottom: 0;
}
</style>
